import React, { useState, useEffect } from "react";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";
import { User } from "./KpiMetricMain";
import { FaUserPlus } from "react-icons/fa";
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  arrayUnion,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "../../firebase/firebase";
import { logToSystem } from "../../utils/systemLogging";
import { IconWrapper } from "../../components/IconWrapper";

interface Metric {
  id: string;
  metric: string;
  description?: string;
  target: string;
  actual: string;
  status: string;
  assignedTo: string[]; // Store user IDs
}

interface MetricFormProps {
  addMetric: (metric: Metric) => void;
  users: User[];
  color: string;
  bg: string;
  organizationId: string;
  isUserSelectOpen: boolean;
  setIsUserSelectOpen: (open: boolean) => void;
  selectedUsers: string[];
  setSelectedUsers: (users: string[]) => void;
  handleUserSelect: (userId: string) => void;
  removeAssignedUser: (userId: string) => void;
  isAdmin?: boolean;
}

const MetricForm: React.FC<MetricFormProps> = ({
  addMetric,
  users,
  color,
  bg,
  organizationId,
  isUserSelectOpen,
  setIsUserSelectOpen,
  selectedUsers,
  setSelectedUsers,
  handleUserSelect,
  removeAssignedUser,
  isAdmin = false,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const [newMetric, setNewMetric] = useState<Metric>({
    id: "",
    metric: "",
    description: "",
    target: "",
    actual: "",
    status: "⚠️",
    assignedTo: [],
  });
  const [loading, setLoading] = useState(false);
  const [toastMsg, setToastMsg] = useState<null | { title: string; description: string; status: string }>();

  // Add a ref to store the current form state
  const formStateRef = React.useRef(newMetric);

  // Update the ref whenever newMetric changes
  useEffect(() => {
    formStateRef.current = newMetric;
  }, [newMetric]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMetric({ ...newMetric, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!newMetric.metric || !newMetric.target) {
      setToastMsg({
        title: "Validation Error",
        description: "Metric name and Target are required.",
        status: "error",
      });
      return;
    }
    setLoading(true);
    try {
      const metricToAdd = {
        ...newMetric,
        assignedTo: selectedUsers,
      };
      const metricId = new Date().getTime().toString();
      const commonMetricsRef = doc(
        db,
        `organization/${organizationId}/Metrics/commonKpiMetrics`
      );
      const commonMetricsSnapshot = await getDoc(commonMetricsRef);
      if (!commonMetricsSnapshot.exists()) {
        await setDoc(commonMetricsRef, { metrics: [] });
      }
      const { id, ...metricWithoutId } = metricToAdd;
      await updateDoc(commonMetricsRef, {
        metrics: arrayUnion({
          id: metricId,
          ...metricWithoutId,
          type: "common",
          createdAt: new Date(),
          createdBy: auth.currentUser?.uid,
        }),
      });
      for (const userId of selectedUsers) {
        const userKpiRef = doc(
          db,
          `organization/${organizationId}/Metrics/userKpiMetrics`
        );
        const userKpiSnapshot = await getDoc(userKpiRef);
        if (!userKpiSnapshot.exists()) {
          await setDoc(userKpiRef, { users: {} });
        }
        const { id, ...metricWithoutId } = metricToAdd;
        await updateDoc(userKpiRef, {
          [`users.${userId}`]: arrayUnion({
            id: new Date().getTime().toString(),
            mainId: metricId,
            ...metricWithoutId,
            type: "common",
            createdAt: new Date(),
            createdBy: auth.currentUser?.uid,
          }),
        });
      }
      addMetric({
        ...metricToAdd,
        id: metricId,
      });
      setToastMsg({
        title: "Metric Added",
        description: "The new metric has been successfully added.",
        status: "success",
      });

      // Log KPI metric creation activity
      try {
        const currentUserAuth = auth.currentUser;
        if (currentUserAuth && organizationId) {
          const userRef = doc(db, "users", currentUserAuth.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            await logToSystem(organizationId, "actions", {
              type: "ACTIVITY",
              user: `${userData.firstName} ${userData.lastName}`,
              email: userData.email,
              action: "KPI Management",
              details: `Added KPI metric ${metricToAdd.metric} with target ${metricToAdd.target} assigned to ${selectedUsers.length} users`,
              status: "success",
            });
          }
        }
      } catch (logError) {
        console.error("Error logging KPI metric creation:", logError);
      }

      // Reset form only on successful submission
      setNewMetric({
        id: "",
        metric: "",
        description: "",
        target: "",
        actual: "",
        status: "⚠️",
        assignedTo: [],
      });
      setSelectedUsers([]);
    } catch (error) {
      setToastMsg({
        title: "Submission Failed",
        description: `❌ Failed to add Metric: ${error instanceof Error ? error.message : "Unknown error"
          }`,
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Attempt to preserve state when modal closes
  // useEffect(() => {
  //   if (!isUserSelectOpen) {
  //     // This effect runs when the modal closes.
  //     // At this point, the state *should* already be correct if it wasn't lost.
  //     // If the state is being lost due to re-initialization from the parent,
  //     // we might not be able to fully restore it here without a saved copy.
  //     // However, this useEffect can be a point to inspect the state or attempt
  //     // to trigger a re-render that might correct it if the issue is subtle.
  //     console.log('Modal closed. Current newMetric state:', newMetric);
  //   }
  // }, [isUserSelectOpen, newMetric]);

  return (
    <div className={`px-6 rounded-xl mb-6 ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white'}`}>
      {/* Toast Message */}
      {toastMsg && (
        <div
          className={`mb-4 px-4 py-2 rounded text-sm font-medium flex items-start justify-between gap-4 ${toastMsg.status === "success"
            ? "bg-green-50 text-green-800 border border-green-200"
            : "bg-red-50 text-red-800 border border-red-200"
            }`}
        >
          <div className="pr-2">
            <div className="font-bold">{toastMsg.title}</div>
            <div>{toastMsg.description}</div>
          </div>
          <button
            type="button"
            className="ml-auto text-xl -mr-1 p-3 leading-none text-current hover:opacity-70"
            aria-label="Dismiss message"
            onClick={() => setToastMsg(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Metric Form */}
      {isAdmin ? (
      <form
        className={`flex flex-col md:flex-row gap-5 items-stretch md:items-center rounded-lg p-4 ${isDark ? 'bg-[#14161B]' : 'bg-white'}`}
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="flex-1">
          <input
            autoComplete="off"
            className={`w-full px-4 py-2 rounded-lg focus:outline-none text-sm transition ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143]' : 'bg-white border border-gray-200 focus:border-[#B59143] focus:ring-2 focus:ring-[#FBD282]'}`}
            placeholder="Metric Name"
            name="metric"
            value={newMetric.metric}
            onChange={handleChange}
          />
        </div>
        <div className="flex-1">
          <input
            autoComplete="off"
            className={`w-full px-4 py-2 rounded-lg focus:outline-none text-sm transition ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143]' : 'bg-white border border-gray-200 focus:border-[#B59143] focus:ring-2 focus:ring-[#FBD282]'}`}
            placeholder="Description (optional)"
            name="description"
            value={newMetric.description || ""}
            onChange={handleChange}
          />
        </div>
        <div className="flex gap-4">
          <input
            autoComplete="off"
            className={`w-32 px-4 py-2 rounded-lg focus:outline-none text-sm transition ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143]' : 'bg-white border border-gray-200 focus:border-[#B59143] focus:ring-2 focus:ring-[#FBD282]'}`}
            placeholder="Target"
            name="target"
            value={newMetric.target}
            onChange={handleChange}
          />
          <input
            autoComplete="off"
            className={`w-32 px-4 py-2 rounded-lg focus:outline-none text-sm transition ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143]' : 'bg-white border border-gray-200 focus:border-[#B59143] focus:ring-2 focus:ring-[#FBD282]'}`}
            placeholder="Actual"
            name="actual"
            value={newMetric.actual}
            onChange={handleChange}
          />
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              type="button"
              className={`flex items-center justify-center w-11 h-11 rounded-full transition relative group text-sm border-2 border-[#B59143] ${isDark ? 'bg-[#2A3441] text-[#FBD282] hover:bg-[#3A4553]' : 'bg-[#FFF7E6] text-[#B59143] hover:bg-[#FDE8C0]'}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsUserSelectOpen(true);
              }}
              title="Select Users"
            >
              {FaUserPlus({ size: 20 })}
              <span className={`absolute left-1/2 -translate-x-1/2 top-12 text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10 ${isDark ? 'bg-[#1A1C1F] text-white border border-[#2E2F33]' : 'bg-gray-800 text-white'}`}>Select Users</span>
            </button>
          )}
          <button
            type="submit"
            className={`px-6 py-2 rounded-lg font-medium text-sm focus:outline-none transition disabled:opacity-60 ${isDark ? 'bg-[#0B0B0F] text-[#FBD282] border-2 border-[#B59143] hover:bg-[#1A1C1F]' : 'bg-[#FFF7E6] text-[#B59143] border-2 border-[#B59143] hover:bg-[#FDE8C0]'}`}
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>...</span>
            ) : (
              "Add"
            )}
          </button>
        </div>
      </form>
      ) : null}

      {/* Assigned Users Badges */}
      {isAdmin && selectedUsers.length > 0 && (
        <div className={`mt-4 p-3 rounded-lg flex flex-wrap gap-2 ${isDark ? 'bg-[#14161B] border border-[#2E2F33]' : 'bg-gray-50 border border-gray-100'}`}>
          <span className={`text-xs mr-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Assigned Users:</span>
          {selectedUsers.map((userId) => {
            const user = users.find((u) => u.id === userId);
            return user ? (
              <span key={user.id} className={`inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full border ${isDark ? 'bg-[#2A3441] text-[#FBD282] border-[#3A4553]' : 'bg-[#FFF7E6] text-[#B59143] border-[#E8C16C]'}`}>
                {user.firstName} {user.lastName}
                <button
                  type="button"
                  className={`${isDark ? 'ml-1 text-[#FBD282] hover:text-red-500' : 'ml-1 text-[#B59143] hover:text-red-500'}`}
                  onClick={() => removeAssignedUser(user.id)}
                  title="Remove"
                >
                  ×
                </button>
              </span>
            ) : null;
          })}
        </div>
      )}
      {/* User selection modal is rendered in KpiMetricMain to avoid duplicates */}
    </div>
  );
};

export default MetricForm;
